import type { AdvisorNotification, AdvisorNotificationPreferences } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  getNotificationPreferences,
  getUnreadNotifications,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  sendPresenceHeartbeat,
  updateNotificationPreferences,
} from "@/lib/api/notifications";

/**
 * QL-728 — advisor notification hooks. Everything lives under the
 * ["notifications"] key family, so one prefix invalidation refreshes the bell,
 * the list and the opt-ins together.
 */
const KEYS = {
  all: ["notifications"] as const,
  unread: ["notifications", "unread"] as const,
  list: ["notifications", "list"] as const,
  preferences: ["notifications", "preferences"] as const,
};

/** How often the bell polls — the same 15 s cadence as the sidebar's unread badges. */
const POLL_MS = 15_000;
const HEARTBEAT_MS = 15_000;
/** How long the API keeps a hidden tab "present" (PRESENCE_TTL_SECONDS): no push is sent in that window. */
const PRESENCE_GRACE_MS = 45_000;

/** Unread count + latest unread, polled every 15 s while signed in. */
export function useUnreadNotifications(enabled: boolean) {
  return useQuery({
    queryKey: KEYS.unread,
    queryFn: getUnreadNotifications,
    refetchInterval: POLL_MS,
    enabled,
  });
}

/** The first page of the inbox; fetched only while the bell's list is open. */
export function useNotificationList(enabled: boolean) {
  return useQuery({
    queryKey: KEYS.list,
    queryFn: () => listNotifications(),
    enabled,
    // The list is opened to see what is new: never serve a cached page, and keep
    // it in step with the unread poll while it stays open.
    staleTime: 0,
    refetchInterval: POLL_MS,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useNotificationPreferences() {
  return useQuery({ queryKey: KEYS.preferences, queryFn: getNotificationPreferences });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: AdvisorNotificationPreferences) =>
      updateNotificationPreferences(preferences),
    onSuccess: (saved) => {
      queryClient.setQueryData(KEYS.preferences, saved);
      return queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

/**
 * Whether this tab counts as "QP is open" for the advisor's notifications: a
 * signed-in app page of the user's own tenant that shows the bell. Standalone
 * (QL-33, embedded without the app chrome) has no bell or toast, so it counts
 * as away and the notification goes to the phone instead of nowhere.
 */
export function presenceHeartbeatEnabled(tab: {
  signedIn: boolean;
  foreignHost: boolean;
  publicPage: boolean;
  standalone: boolean;
}): boolean {
  return tab.signedIn && !tab.foreignHost && !tab.publicPage && !tab.standalone;
}

/**
 * Tell the API a QP tab is visible, every 15 s and at once on becoming
 * visible, so notifications show here instead of being pushed to the phone. A
 * hidden tab sends nothing and lapses to "away" within 45 s.
 */
export function usePresenceHeartbeat(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const beat = () => {
      if (document.visibilityState !== "visible") return;
      // Presence is advisory: a failed beat only means the next push reaches the phone.
      sendPresenceHeartbeat().catch(() => {});
    };
    beat();
    const timer = window.setInterval(beat, HEARTBEAT_MS);
    document.addEventListener("visibilitychange", beat);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", beat);
    };
  }, [enabled]);
}

/**
 * Call `onArrive` once for each unread notification a poll brings in while this
 * tab is visible, oldest first. Not news, so only recorded:
 * - the first answer (a page load);
 * - an answer while the tab is hidden;
 * - in the first answer after the tab becomes visible again, what was created
 *   once the hidden tab's presence had lapsed (45 s after it was hidden). The
 *   15 s poll skips its ticks while hidden (React Query's
 *   refetchIntervalInBackground is off), so that answer holds what arrived
 *   while the advisor was away — already pushed to the phone. It stays in the
 *   unread count and the list. What was created within those 45 s got no push
 *   (the API still counted the tab as present), so it is announced on return.
 *   `createdAt` is the server's clock, so clock skew moves that cutoff slightly.
 *
 * `fetchedAt` is the query's `dataUpdatedAt`: it moves on every successful
 * poll, even one that returns the same items. `onArrive` must be stable.
 */
export function useNotificationArrivals(
  latest: AdvisorNotification[] | undefined,
  fetchedAt: number,
  onArrive: (notification: AdvisorNotification) => void,
) {
  const seen = useRef<Set<string> | null>(null);
  // When the tab last became visible, until a poll answered after it.
  const visibleAgainAt = useRef<number | null>(null);
  // When the tab was last hidden.
  const hiddenAt = useRef<number | null>(null);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") visibleAgainAt.current = Date.now();
      else hiddenAt.current = Date.now();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (!latest) return;
    if (seen.current === null) {
      seen.current = new Set(latest.map((n) => n.id));
      return;
    }
    const resync = visibleAgainAt.current !== null && fetchedAt >= visibleAgainAt.current;
    if (resync) visibleAgainAt.current = null;
    const visible = document.visibilityState === "visible";
    // On resync, only what arrived while presence still held was kept off the phone.
    const pushedFrom = hiddenAt.current === null ? null : hiddenAt.current + PRESENCE_GRACE_MS;
    for (const n of [...latest].reverse()) {
      if (seen.current.has(n.id)) continue;
      seen.current.add(n.id);
      const notPushed = !resync || pushedFrom === null || Date.parse(n.createdAt) < pushedFrom;
      if (visible && notPushed) onArrive(n);
    }
  }, [latest, fetchedAt, onArrive]);
}
